import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useRbac } from '../../context/RbacContext';

const STATUS_CONFIG = {
  PENDING:               { color: '#e67e22', bg: '#fef9f0', label: 'Pending' },
  PAID:                  { color: '#27ae60', bg: '#f0faf4', label: 'Paid' },
  INSURANCE_PROCESSING:  { color: '#2980b9', bg: '#eaf4fb', label: 'Insurance' },
  OVERDUE:               { color: '#c0392b', bg: '#fdf2f2', label: 'Overdue' },
  VOIDED:                { color: '#95a5a6', bg: '#f8f9fa', label: 'Voided' },
  PARTIAL:               { color: '#8e44ad', bg: '#f5eefb', label: 'Partial' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { color: '#555', bg: '#f0f0f0', label: status };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}33`,
      borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700,
    }}>{cfg.label}</span>
  );
}

export default function BillingDashboard() {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 20;
  const navigate = useNavigate();
  const { can } = useRbac();
  const isAdmin = can('page.billing_admin');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: PER_PAGE });
      if (status) params.append('status', status);
      const billRes = await api.get(`/billing/?${params}`);
      setBills(billRes.data.data || []);
      setTotal(billRes.data.total || 0);
    } catch {
      setBills([]);
    } finally {
      setLoading(false);
    }
    // Summary fetched independently so a 403 on /summary doesn't wipe the bills list
    if (isAdmin) {
      api.get('/billing/summary')
        .then(r => setSummary(r.data.data || {}))
        .catch(() => {});
    }
  }, [page, status, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `KES ${Number(n || 0).toLocaleString()}`;

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#2b1b22' }}>Billing & Payments</h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>
            Invoices, M-Pesa, card payments and insurance claims
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <>
              <button onClick={() => navigate('/billing/rate-cards')}
                style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#2b1b22' }}>
                Rate Cards
              </button>
              <button onClick={() => navigate('/billing/insurance')}
                style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#2b1b22' }}>
                Insurance
              </button>
              <button onClick={() => navigate('/billing/config')}
                style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#2b1b22' }}>
                ⚙ Config
              </button>
              <button onClick={() => navigate('/billing/new')}
                style={{ background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>
                + New Invoice
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary cards — admin/billing staff see system-wide totals; patients see their own count */}
      {(() => {
        const cards = isAdmin ? [
          { label: 'Total Billed',     value: fmt(summary.total_billed),              color: '#2b1b22' },
          { label: 'Total Collected',  value: fmt(summary.total_paid),                color: '#27ae60' },
          { label: 'Pending Invoices', value: summary.total_pending || 0,             color: '#e67e22' },
          { label: 'Collection Rate',  value: `${summary.collection_rate || 0}%`,     color: '#2980b9' },
        ] : [
          { label: 'My Invoices',      value: total,                                  color: '#2b1b22' },
          { label: 'Pending Payment',  value: summary.total_pending || 0,             color: '#e67e22' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, 1fr)`, gap: 14, marginBottom: 24 }}>
            {cards.map(c => (
              <div key={c.label} className="hhms-card" style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#8b7681', textTransform: 'uppercase', marginBottom: 6 }}>{c.label}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Payment method breakdown — admin/billing staff only */}
      {isAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'M-Pesa Payments', value: summary.mpesa_payments || 0, icon: '📱', color: '#27ae60' },
            { label: 'Card Payments', value: summary.card_payments || 0, icon: '💳', color: '#2980b9' },
            { label: 'Insurance Claims', value: summary.insurance_claims_processing || 0, icon: '🏥', color: '#8e44ad' },
          ].map(c => (
            <div key={c.label} className="hhms-card" style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 32 }}>{c.icon}</div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
                <div style={{ fontSize: 11, color: '#8b7681', fontWeight: 600 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {['', 'PENDING', 'PAID', 'PARTIAL', 'INSURANCE_PROCESSING', 'OVERDUE', 'VOIDED'].map(s => (
          <button key={s} onClick={() => { setStatus(s); setPage(1); }}
            style={{
              padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              border: `1px solid ${status === s ? '#7b1e3c' : '#ddd'}`,
              background: status === s ? '#7b1e3c' : '#fff',
              color: status === s ? '#fff' : '#555', cursor: 'pointer',
            }}>{s || 'All'}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8b7681' }}>{total} invoices</span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f5f6' }}>
              {['Invoice #', 'Patient', 'Amount', 'Copay', 'Method', 'Status', 'Due', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#8b7681', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>Loading…</td></tr>
            ) : bills.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>No invoices found.</td></tr>
            ) : bills.map((b, i) => (
              <tr key={b.billing_id} className="hhms-table-row" style={{ borderTop: i === 0 ? 'none' : '1px solid #f0ebed' }}>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#7b1e3c' }}>{b.invoice_number}</td>
                <td style={{ padding: '10px 14px', color: '#2b1b22' }}>{b.patient_name || b.patient_id?.slice(0, 8) || '—'}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>KES {Number(b.service_cost).toLocaleString()}</td>
                <td style={{ padding: '10px 14px' }}>KES {Number(b.patient_copay).toLocaleString()}</td>
                <td style={{ padding: '10px 14px', color: '#555' }}>{b.payment_method || '—'}</td>
                <td style={{ padding: '10px 14px' }}><StatusBadge status={b.payment_status} /></td>
                <td style={{ padding: '10px 14px', color: '#8b7681', fontSize: 12 }}>{b.due_date || '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => navigate(`/billing/${b.billing_id}`)}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#7b1e3c', fontWeight: 600 }}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PER_PAGE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>← Prev</button>
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#555' }}>Page {page} of {Math.ceil(total / PER_PAGE)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / PER_PAGE)}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', background: '#fff' }}>Next →</button>
        </div>
      )}
    </div>
  );
}
